import React, { useState, useEffect } from 'react';
import { getFoodThumbnail } from '@/config/motivationalImages';
import { Utensils, Leaf, Drumstick, Milk, Wheat, Croissant, Snowflake, Wine, Flame, Cookie, Archive } from 'lucide-react';

const API = '/api';

// Category-specific fallback icons for grocery items
const CATEGORY_ICONS = {
  Produce:    { Icon: Leaf, color: '#5A8C6A' },
  Protein:    { Icon: Drumstick, color: '#C67A5C' },
  Dairy:      { Icon: Milk, color: '#4A90D9' },
  Grains:     { Icon: Wheat, color: '#E5A84B' },
  Bakery:     { Icon: Croissant, color: '#C9956B' },
  Frozen:     { Icon: Snowflake, color: '#6BB5E5' },
  Beverages:  { Icon: Wine, color: '#7B68AE' },
  Spices:     { Icon: Flame, color: '#D4736A' },
  Snacks:     { Icon: Cookie, color: '#9B59B6' },
  Pantry:     { Icon: Archive, color: '#6A6560' },
};

/**
 * Smart food thumbnail with priority fallback:
 * 1. User's meal photo (thumbnailPath or photoPath)
 * 2. Batch meal photo
 * 3. Category-matched stock thumbnail (from Unsplash)
 * 4. Generic food icon (last resort)
 *
 * Props:
 *   foodName - string, name of the food item for category matching
 *   thumbnailPath - string, user's meal thumbnail storage path
 *   mealId - string, for constructing thumbnail API URL
 *   token - string, auth token for thumbnail API
 *   size - 'sm' (48px) | 'md' (64px) | 'lg' (120px)
 *   className - extra CSS classes
 */
export const FoodThumbnail = ({
  foodName = '',
  thumbnailPath = '',
  mealId = '',
  token = '',
  size = 'md',
  className = '',
  circular = false,
  responsiveSize,
  category = '', // grocery category for fallback icon
  'data-testid': dataTestId,
}) => {
  const [imgError, setImgError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [blurLoaded, setBlurLoaded] = useState(false);
  const [genImgError, setGenImgError] = useState(false);

  const sizeMap = { sm: 'w-11 h-11', md: 'w-[52px] h-[52px]', lg: 'w-[120px] h-[120px]', xl: 'w-[60px] h-[60px]', fill: 'w-full h-full' };
  const sizeClass = sizeMap[size] || sizeMap.md;

  // Determine responsive size based on component size if not specified
  const resolvedResponsiveSize = responsiveSize || (size === 'lg' ? 'medium' : 'small');

  // Determine image source
  let src = '';
  let blurSrc = '';
  if (!imgError && mealId && token) {
    if (thumbnailPath) {
      // Try responsive WebP first, fallback to thumbnail
      src = `${API}/meals/${mealId}/image/${resolvedResponsiveSize}?auth=${token}`;
      blurSrc = `${API}/meals/${mealId}/image/blur?auth=${token}`;
    }
  }

  // Fallback to category stock
  const stockThumb = getFoodThumbnail(foodName);
  const fallbackSrc = stockThumb.url;

  // Generated image URL (tried when stock is generic)
  const generatedSrc = (stockThumb.isGeneric && foodName)
    ? `${API}/food-images/generated/${encodeURIComponent(foodName.toLowerCase().trim())}`
    : '';

  // Report missing image when using a generic fallback (fires once per food name per session)
  useEffect(() => {
    if (foodName && fallbackSrc && stockThumb.isGeneric) {
      const key = `reported_${foodName.toLowerCase()}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        fetch(`${API}/meals/report-missing-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ food_name: foodName }),
        }).catch(() => {});
      }
    }
  }, [foodName, fallbackSrc, stockThumb.isGeneric]);

  const handleError = () => {
    setImgError(true);
  };

  // Priority: user photo → generated AI image (if stock is generic) → stock image
  let finalSrc;
  if (src && !imgError) {
    finalSrc = src;
  } else if (generatedSrc && !genImgError) {
    finalSrc = generatedSrc;
  } else {
    finalSrc = fallbackSrc;
  }

  return (
    <div
      className={`${sizeClass} overflow-hidden flex-shrink-0 relative ${className}`}
      data-testid={dataTestId}
      style={{
        borderRadius: circular ? '50%' : '14px',
        background: 'linear-gradient(135deg, #F9E8D9, #F5DFC9)',
      }}
    >
      {/* Blur placeholder (loads before main image) */}
      {blurSrc && !loaded && (
        <img
          src={blurSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-md scale-110"
          onLoad={() => setBlurLoaded(true)}
          aria-hidden="true"
        />
      )}
      {/* Shimmer loading state */}
      {!loaded && !blurLoaded && (
        <div className="absolute inset-0 animate-pulse" style={{ background: 'linear-gradient(135deg, #F9E8D9, #F5DFC9)' }} />
      )}
      {finalSrc ? (
        <img
          src={finalSrc}
          alt={foodName || 'Meal'}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ filter: 'saturate(1.1) brightness(1.05) sepia(0.05)' }}
          onError={() => {
            // Cascade: user photo → generated → stock
            if (src && !imgError) {
              setImgError(true);
            } else if (generatedSrc && !genImgError) {
              setGenImgError(true);
            }
          }}
          onLoad={() => setLoaded(true)}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {(() => {
            const catIcon = category && CATEGORY_ICONS[category];
            if (catIcon) {
              const { Icon, color } = catIcon;
              return <Icon className="w-5 h-5" style={{ color }} />;
            }
            return <Utensils className="w-5 h-5" style={{ color: '#C67A5C' }} />;
          })()}
        </div>
      )}
    </div>
  );
};

export default FoodThumbnail;
